import { History, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { ModalBackdrop, PaginationFooter } from '../common';

const HISTORY_LOG_PAGE_SIZE = 5;

function PurchaseOrderHistoryModal({
  purchaseOrder,
  historyLogs,
  isLoading,
  error,
  onBack,
  onClose,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const totalLogs = historyLogs.length;
  const totalPages = Math.max(1, Math.ceil(totalLogs / HISTORY_LOG_PAGE_SIZE));
  const normalizedPage = Math.min(currentPage, totalPages);
  const startIndex = (normalizedPage - 1) * HISTORY_LOG_PAGE_SIZE;
  const endIndex = Math.min(startIndex + HISTORY_LOG_PAGE_SIZE, totalLogs);
  const visibleLogs = useMemo(
    () => historyLogs.slice(startIndex, endIndex),
    [endIndex, historyLogs, startIndex]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [historyLogs]);

  return (
    <ModalBackdrop backdropClassName="bg-[rgba(33,27,22,0.28)]">
      <div className="mx-auto w-full max-w-3xl rounded-[24px] border border-[var(--theme-border-soft)] bg-[linear-gradient(180deg,var(--theme-surface-warm)_0%,var(--theme-surface-muted)_100%)] shadow-[0_24px_60px_rgba(var(--theme-shadow-rgb),0.16)] sm:rounded-[28px]">
        <div className="flex items-start justify-between gap-4 border-b border-[#efe5dd] px-4 py-4 sm:px-6 sm:py-4.5">
          <div>
            <p className="m-0 text-[0.78rem] font-semibold uppercase tracking-[0.09em] text-[var(--theme-text-muted)]">
              Purchase Order History
            </p>
            <h2 className="m-0 mt-1.5 text-[1.2rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)] sm:text-[1.35rem]">
              Edit and Status Logs
            </h2>
            <p className="m-0 mt-1.5 text-[0.93rem] leading-6 text-[var(--theme-text-muted)]">
              Review changes made to{' '}
            <span className="rounded-[10px] bg-[var(--theme-accent-soft)] px-2 py-0.5 font-semibold text-[var(--theme-text-strong)]">
                {purchaseOrder.supplierName}
              </span>
              &apos;s purchase order for audit and tracing.
            </p>
          </div>

          <button
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#e6dbd0] bg-white text-[#6e6257] transition hover:bg-[var(--theme-surface-soft)]"
            type="button"
            onClick={onClose}
            aria-label="Close purchase order history modal"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="px-4 py-4 sm:px-6 sm:py-5">
          {error ? (
            <div className="rounded-[18px] border border-[#f0ddd8] bg-[#fbf3f1] px-4 py-3 text-[0.92rem] text-[#9a5649]">
              {error}
            </div>
          ) : isLoading ? (
            <div className="rounded-[18px] border border-[var(--theme-border-soft)] bg-white px-4 py-10 text-center text-[var(--theme-text-muted)]">
              Loading history logs...
            </div>
          ) : historyLogs.length ? (
            <div className="grid gap-2.5">
              {visibleLogs.map((log) => (
                <div
                  key={log.id}
                  className="rounded-[14px] border border-[var(--theme-border-soft)] bg-white px-3.5 py-2.5"
                >
                  <div className="flex flex-wrap items-center justify-between gap-x-2.5 gap-y-1.5">
                    <strong className="text-[0.88rem] text-[var(--theme-text)]">
                      {formatActionLabel(log.actionType, log.fieldName)}
                    </strong>
                    <span className="text-[0.76rem] uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
                      {formatTimestamp(log.createdAt)}
                    </span>
                  </div>
                  <p className="m-0 mt-1.5 text-[0.82rem] leading-5 text-[#5b5048]">
                    {log.previousValue
                      ? <>
                          <span className="text-[var(--theme-text-muted)]">From:</span> {log.previousValue}
                          <span className="mx-2 text-[#c3b5a8]">→</span>
                        </>
                      : null}
                    <span className="text-[var(--theme-text-muted)]">To:</span> {log.nextValue || '-'}
                  </p>
                  <p className="m-0 mt-1 text-[0.76rem] text-[var(--theme-text-muted)]">
                    <span className="text-[var(--theme-text-muted)]">Transacted by:</span>{' '}
                    {log.changedByName || log.changedByUserId || 'Unknown user'}
                  </p>
                </div>
              ))}
              <div className="overflow-hidden rounded-[16px] border border-[var(--theme-border-soft)]">
                <PaginationFooter
                  currentPage={normalizedPage}
                  endItem={endIndex}
                  isNextDisabled={normalizedPage >= totalPages}
                  isPreviousDisabled={normalizedPage <= 1}
                  onNext={() => setCurrentPage((page) => Math.min(page + 1, totalPages))}
                  onPrevious={() => setCurrentPage((page) => Math.max(page - 1, 1))}
                  startItem={startIndex + 1}
                  totalItems={totalLogs}
                  totalPages={totalPages}
                />
              </div>
            </div>
          ) : (
            <div className="rounded-[18px] border border-dashed border-[#e6ddd4] bg-[var(--theme-app-bg)] px-5 py-10">
              <div className="flex flex-col items-center justify-center text-center text-[var(--theme-text-muted)]">
                <div className="grid h-14 w-14 place-items-center rounded-full bg-[var(--theme-accent-soft)] text-[var(--theme-text-muted)]">
                  <History className="h-6 w-6" aria-hidden="true" />
                </div>
                <strong className="mt-4 text-[1rem] font-medium text-[var(--theme-text-strong)]">
                  No history logs yet
                </strong>
              </div>
            </div>
          )}

          <div className="mt-4 flex justify-end">
            <button
              className="rounded-full border border-[var(--theme-border)] bg-[var(--theme-accent-soft)] px-6 py-2.5 text-[0.92rem] font-medium text-[var(--theme-accent-strong)] transition hover:border-[var(--theme-accent)] hover:bg-[var(--theme-accent-panel)] hover:text-[var(--theme-accent-panel-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)] focus-visible:ring-offset-2"
              type="button"
              onClick={onBack}
            >
              Back
            </button>
          </div>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function formatActionLabel(actionType, fieldName) {
  if (actionType === 'created') {
    return 'Purchase order created';
  }

  if (actionType === 'viewed_without_changes') {
    return 'No changes saved';
  }

  if (fieldName === 'status') {
    return 'Status updated';
  }

  return `${formatFieldLabel(fieldName)} updated`;
}

function formatFieldLabel(fieldName) {
  const fieldLabels = {
    poDate: 'P.O. Date',
    supplierName: 'Supplier',
    price: 'Initial price',
    actualPrice: 'Actual price',
    quantity: 'Quantity',
    receivedQuantity: 'Received quantity',
    stockUnit: 'Stock unit',
    lansaName: 'Lansa name',
    sacksMark: 'Sacks mark',
    deliveryDate: 'Delivery date',
  };

  if (fieldLabels[fieldName]) {
    return fieldLabels[fieldName];
  }

  return String(fieldName || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/^./, (character) => character.toUpperCase());
}

function formatTimestamp(value) {
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default PurchaseOrderHistoryModal;
